import { createVar } from '@vanilla-extract/css';
import { lightBlue } from '../palette';
import { themeVars } from '../theme/vars';
import { borderRadius, space } from '../tokens';
import { typography } from '../typography';
import { globalStyle, style, styleVariants } from '../layer.css';

export const buttonSize = styleVariants({
  xSmall: {
    ...typography['labelSm'],
    minHeight: 24,
    padding: `${space.s2}px ${space.s8}px`,
  },
  small: {
    ...typography['labelMd'],
    minHeight: 32,
    padding: `${space.s4}px ${space.s12}px`,
  },
  medium: {
    ...typography['labelMd'],
    minHeight: 40,
    padding: `${space.s8}px ${space.s16}px`,
  },
  large: {
    ...typography['labelLg'],
    minHeight: 48,
    padding: `${space.s12}px ${space.s24}px`,
  },
});

globalStyle(`.${buttonSize.xSmall} :where(svg)`, {
  height: 14,
  width: 14,
});

globalStyle(`.${buttonSize.small} :where(svg)`, {
  height: 16,
  width: 16,
});

globalStyle(`.${buttonSize.medium} :where(svg)`, {
  height: 20,
  width: 20,
});

globalStyle(`.${buttonSize.large} :where(svg)`, {
  height: 20,
  width: 20,
});

export const buttonText = createVar();
export const buttonBg = createVar();
export const buttonBgHover = createVar();
export const buttonBgActive = createVar();

export const buttonVariants = styleVariants({
  primary: {
    vars: {
      [buttonBg]: themeVars.button.primary.background.idle,
      [buttonBgHover]: themeVars.button.primary.background.hover,
      [buttonBgActive]: themeVars.button.primary.background.active,
      [buttonText]: themeVars.button.primary.foreground,
    },

    selectors: {
      '&[data-inversed]': {
        vars: {
          [buttonBg]: themeVars.button.inverted.background.idle,
          [buttonBgHover]: themeVars.button.inverted.background.hover,
          [buttonBgActive]: themeVars.button.inverted.background.active,
          [buttonText]: themeVars.button.inverted.foreground,
        },
      },
    },
  },

  secondary: {
    vars: {
      [buttonBg]: themeVars.button.secondary.background.idle,
      [buttonBgHover]: themeVars.button.secondary.background.hover,
      [buttonBgActive]: themeVars.button.secondary.background.active,
      [buttonText]: themeVars.button.secondary.foreground,
    },
  },

  ghost: {
    vars: {
      [buttonBg]: 'transparent',
      [buttonBgHover]: themeVars.button.tertiary.background.hover,
      [buttonBgActive]: themeVars.button.tertiary.background.active,
      [buttonText]: themeVars.button.tertiary.foreground,
    },

    selectors: {
      '&[data-inversed]': {
        vars: {
          [buttonText]: themeVars.button.primary.foreground,
          [buttonBgHover]: 'transparent',
          [buttonBgActive]: 'transparent',
        },
      },
      '&[data-inversed]:where(:hover, :active)': {
        textDecoration: 'underline',
      },
    },
  },

  outline: {
    vars: {
      [buttonBg]: 'transparent',
      [buttonBgHover]: themeVars.button.tertiary.background.hover,
      [buttonBgActive]: themeVars.button.tertiary.background.active,
      [buttonText]: themeVars.button.tertiary.foreground,
    },
    selectors: {
      '&:not(:disabled)': {
        boxShadow: `inset 0 0 0 1px ${themeVars.button.tertiary.border}`,
      },
    },
  },

  critical: {
    vars: {
      [buttonBg]: themeVars.color.bg.alert.critical.default,
      [buttonBgHover]: themeVars.color.bg.alert.critical.hover,
      [buttonBgActive]: themeVars.color.bg.alert.critical.active,
      [buttonText]: themeVars.color.text.inverse,
    },
  },
});
export type ButtonVariant = keyof typeof buttonVariants;

export const buttonFullWidth = style({
  width: '100%',
});

export const button = style({
  cursor: 'pointer',
  borderRadius: borderRadius.r8,
  border: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: space.s6,
  position: 'relative',
  transitionDuration: '0.15s',
  transitionProperty: 'background-color, color, border, opacity',
  textDecoration: 'none',

  backgroundColor: buttonBg,
  color: buttonText,

  ':hover': {
    backgroundColor: buttonBgHover,
  },
  ':active': {
    backgroundColor: buttonBgActive,
  },

  ':focus': {
    outline: 'none',
  },
  ':focus-visible': {
    outline: `2px solid ${lightBlue[500]}`,
    outlineOffset: '-1px',
  },
  ':disabled': {
    cursor: 'default',
    backgroundColor: themeVars.button.disabled.background,
    color: themeVars.button.disabled.foreground,
  },
  selectors: {
    '&[aria-expanded="true"]': {
      backgroundColor: buttonBgActive,
    },
    [`&:not(${buttonFullWidth})`]: {
      flexShrink: 0,
    },
  },
});
globalStyle(`.${button} > svg`, {
  flexShrink: 0,
});

export const buttonRounded = style({
  borderRadius: borderRadius.r32,
});

export const buttonContent = style({
  display: 'contents',
});

export const buttonSpinner = style({
  position: 'absolute',
});
